import {useState,useCallback,useEffect,useContext} from 'react'
import {useNavigate,useParams} from 'react-router-dom'
import {rt,storage} from '../helpers/firebase'
import {onValue,remove,ref as rtRef,update} from 'firebase/database'
import {deleteObject,ref} from 'firebase/storage'
import {diff} from 'deep-object-diff'
import UserContext from '../contexts/UserContext'
import ErrorContext from '../contexts/ErrorContext'
import ProjectsContext from '../contexts/ProjectsContext'
import useDidMountEffect from './useDidMountEffect'

export default function useProject({
    nodes,
    edges,
    setNodes,
    setEdges,
    chatTabs,
    activeTab,
    loadTabs,
    readOnly,
}) {
    const [user]=useContext(UserContext)
    const setError=useContext(ErrorContext)
    const updateProjects=useContext(ProjectsContext)[1]
    const navigate=useNavigate()
    // uid param is the project OWNER's uid (present on collaborative routes /design/:uid/:projectId)
    // For owned projects (/design/:projectId) it falls back to the current user's uid
    const {projectId, uid: ownerUidParam}=useParams()
    const uid=ownerUidParam||user?.uid

    const [projectLoading,setProjectLoading]=useState(true)
    const [madeChanges,setMadeChanges]=useState(false)
    const [title,setTitle]=useState('Untitled Flowchart')
    const [gridScale,setGridScale]=useState(10)
    const [publicAccess,setPublicAccess]=useState(false)
    const [prevProject,setPrevProject]=useState(null)
    const [lastReceivedProject,setLastReceivedProject]=useState(null)

    // Load project from RTDB
    useEffect(() => {
        if(!projectId) {
            navigate('/dashboard')
        }

        const projectRef=rtRef(rt,`projects/${uid||user?.uid}/${projectId}`)
        onValue(projectRef,(snapshot) => {
            if(!snapshot.exists()) {
                navigate('/dashboard')
                return
            }
            const project=snapshot.val()
            setProjectLoading(false)
            setMadeChanges(false)
            if(project) {
                setNodes(prevNodes => {
                    // Preserve local-only nodes (like reccNodes) that aren't saved to database
                    const localOnlyNodes=prevNodes.filter(n => n.type==='reccNode')
                    const dbNodes=(project.nodes||[]).map((node) => {
                        return {
                            ...node,
                            selected: prevNodes.find((n) => n.id===node.id)?.selected||false
                        }
                    })
                    return [...dbNodes,...localOnlyNodes]
                })
                setEdges(prevEdges => (project.edges||[]).map((edge) => {
                    return {
                        ...edge,
                        selected: prevEdges.find((e) => e.id===edge.id)?.selected||false
                    }
                }))
                setTitle(project.title||'Untitled Flowchart')
                setGridScale(project.gridScale||10)
                // Load chat tabs with backwards compatibility for old projects
                loadTabs(project.chatTabs,project.chats)
                setPublicAccess(project.public||false)
                setLastReceivedProject({
                    nodes: (project.nodes||[]).map((node) => {
                        return {
                            ...node,
                            selected: false
                        }
                    }),
                    edges: (project.edges||[]).map((edge) => {
                        return {
                            ...edge,
                            selected: false
                        }
                    }),
                    title: project.title||'Untitled Flowchart',
                    gridScale: project.gridScale||10,
                    public: project.public||false,
                    chatTabs: project.chatTabs||[],
                    chats: project.chats||[]
                })
            }
        })
    },[projectId,navigate,user,uid,setNodes,setEdges,loadTabs])

    // Save project to RTDB
    const saveProject=useCallback(async () => {
        const projectRef=rtRef(rt,`projects/${uid||user?.uid}/${projectId}`)
        // Filter out local-only nodes (reccNodes) - they shouldn't be saved to database
        const persistableNodes=nodes.filter(n => n.type!=='reccNode')
        const currProject={
            nodes: persistableNodes.map(node => {
                return {
                    ...node,
                    selected: false
                }
            }),
            edges: edges.map(edge => {
                return {
                    ...edge,
                    selected: false
                }
            }),
            title,
            gridScale,
            public: publicAccess,
            chatTabs,
            chats: activeTab.messages // Keep for backwards compatibility
        }
        setPrevProject(currProject)
        return update(projectRef,{
            ...currProject,
            updatedAt: Date.now()
        }).then(() => {
            return Promise.resolve()
        })
    },[projectId,title,uid,user?.uid,nodes,edges,gridScale,publicAccess,chatTabs,activeTab.messages])

    // Auto-save on changes
    useDidMountEffect(() => {
        if(!lastReceivedProject) return
        // Filter out local-only nodes (reccNodes) from autosave
        const persistableNodes=nodes.filter(n => n.type!=='reccNode')
        const currProject={
            nodes: persistableNodes.map(node => {
                return {
                    ...node,
                    selected: false
                }
            }),
            edges: edges.map(edge => {
                return {
                    ...edge,
                    selected: false
                }
            }),
            title,
            gridScale,
            public: publicAccess,
            chatTabs,
            chats: activeTab.messages
        }
        const changes=diff(prevProject||{},currProject)
        if(!Object.keys(changes).length||!Object.keys(diff(lastReceivedProject||{},currProject)).length) {
            return Promise.resolve()
        } else {
            if(!readOnly) {
                saveProject()
            }
        }
    },[publicAccess,chatTabs,nodes,edges,gridScale,projectId])

    // Delete project
    const handleDelete=useCallback(() => {
        if(window.confirm('Are you sure you want to delete this project?')) {
            const projectRef=rtRef(rt,`projects/${uid||user?.uid}/${projectId}`)
            remove(projectRef).then(() => {
                const storageRef=ref(storage,`users/${user.uid}/${projectId}/view.png`)
                deleteObject(storageRef)
                updateProjects()
                navigate('/dashboard')
            })
        } else {
            setError('Project could not be deleted. Please try again later.')
        }
    },[uid,user,projectId,updateProjects,navigate,setError])

    return {
        projectLoading,
        title,
        setTitle,
        gridScale,
        setGridScale,
        publicAccess,
        setPublicAccess,
        madeChanges,
        setMadeChanges,
        saveProject,
        handleDelete,
    }
}
